import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { procurementService } from '@/lib/api/services/procurement'

// Query keys factory
export const procurementKeys = {
  all: ['procurements'] as const,
  lists: () => [...procurementKeys.all, 'list'] as const,
  list: () => [...procurementKeys.lists(), 'list'] as const,
  details: () => [...procurementKeys.all, 'detail'] as const,
  detail: (id: string) => [...procurementKeys.details(), id] as const,
}

/**
 * Hook to fetch paginated procurements with filters
 */
export const useProcurements = ()=> {
  return useQuery({
    queryKey: procurementKeys.list(),
    queryFn: () => procurementService.getProcurements(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook to fetch a single procurement by ID
 */
export const useProcurement = (procurementId: string | undefined) => {
  return useQuery({
    queryKey: procurementKeys.detail(procurementId || ''),
    queryFn: () => procurementService.getProcurementById(procurementId!),
    enabled: !!procurementId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to increment view count for a procurement
 */
export const useIncrementProcurementView = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (procurementId: string) =>
      procurementService.incrementView(procurementId),
    onSuccess: (_, procurementId) => {
      // Invalidate the detail query to refetch with updated view count
      queryClient.invalidateQueries({ queryKey: procurementKeys.detail(procurementId) })
      // Also invalidate list queries to update view counts in lists
      queryClient.invalidateQueries({ queryKey: procurementKeys.lists() })
    },
  })
}

/**
 * Hook to increment download count for a procurement
 */
export const useIncrementProcurementDownload = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (procurementId: string) =>
      procurementService.incrementDownload(procurementId),
    onSuccess: (_, procurementId) => {
      // Invalidate the detail query to refetch with updated download count
      queryClient.invalidateQueries({ queryKey: procurementKeys.detail(procurementId) })
      // Also invalidate list queries to update download counts in lists
      queryClient.invalidateQueries({ queryKey: procurementKeys.lists() })
    },
  })
}

/**
 * Hook to fetch home page highlights
 */
export const useProcurementHighlights = () => {
  return useQuery({
    queryKey: [...procurementKeys.all, 'highlights'],
    queryFn: () => procurementService.getHomePageHighlights(),
    staleTime: 5 * 60 * 1000,
  })
}

